// Shared CRITERIA form for Visuals & Dashboards — the filter prompts a user
// fills before a chart/report/dashboard runs. Rendered identically on every
// surface (designer live preview, in-app Run, downloaded app); each host
// supplies a `VisualCriteriaRuntime` for the two bits of I/O that differ:
//
//   - lookup            → the F2 reference picker (reuses the txn LookupModal),
//   - criteriaItemChange → the on-change rule that fills/locks OTHER criteria.
//
// This mirrors the transaction designer's lookup + item_change on a column,
// but keyed by the criterion's PARAM NAME instead of a form column. It lives in
// the txn package because it reuses LookupModal / TxnRuntime wholesale.

import { issuesFor, type CriteriaIssue } from './criteriaFormat'
import { useCallback, useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import ChipInput from './ChipInput'
import LookupModal from './LookupModal'
import { TxnRuntimeProvider } from './context'
import type { TxnNotifier, TxnRuntime } from './context'
import type { ItemChangeResult, LookupFilterRules } from './types'
import type { LookupRow } from './contracts'
import { tw } from '../testHooks'

// One prompt criterion as surfaced by the backend ``extract_prompt_vars`` /
// dashboard GLOBAL_CRITERIA — plus the optional lookup + item_change config.
export interface VisualCriteriaPromptVar {
  name: string
  label: string
  data_type?: string
  operator?: string
  default?: unknown
  has_default?: boolean
  lookup?: string
  lookup_filter?: string
  lookup_filter_rules?: LookupFilterRules
  lookup_sql?: string
  lookup_title?: string
  lookup_sql_data_col?: string
  has_item_change?: boolean
  // Fixed dropdown (picklist) — renders a <select> of these {data, display}
  // options instead of a free-text input. Takes precedence over a lookup.
  options?: { data: string; display?: string }[]
  // Read-only: the value is shown but the user cannot change it. Used by the
  // enforced-filter bar for a condition the designer locked; a criterion
  // locked dynamically by item_change goes through `protect` instead.
  locked?: boolean
  // Set by the tenant's functional administrator (screen customization):
  // `hidden` keeps the prompt off the form (it always runs on its default),
  // `protect` shows it read-only. The server enforces both — a value sent for
  // such a prompt is ignored.
  hidden?: boolean
  protect?: boolean
  // Placeholder for a single-value input (the enforced-filter bar puts the
  // designer's default there so a cleared input reads as "back to default").
  placeholder?: string
  // For a RANGE: a caption for each bound, shown on its own input in place of
  // the generic "From" / "To". Either may stand alone.
  label_from?: string
  label_to?: string
  // Must have a value before the run: a visual / report prompt with no default
  // (the server says so — its SQL binds it) or a dashboard criterion marked
  // Mandatory. Shown with a *; the bar refuses Apply while it is blank, and the
  // server checks it again.
  required?: boolean
}

export type { CriteriaIssue } from './criteriaFormat'
export { missingRequired, requiredIssues, issuesFor } from './criteriaFormat'

// The host transport: lookup data + the on-change item_change call. ``lookup``
// matches TxnRuntime.lookup verbatim so it drops straight into LookupModal.
// ``criteriaItemChange`` is optional — a surface with no on-change rules (the
// transaction list's enforced-filter bar) supplies only the picker.
export interface VisualCriteriaRuntime {
  lookup: TxnRuntime['lookup']
  criteriaItemChange?: (
    triggerParam: string,
    params: Record<string, unknown>,
  ) => Promise<ItemChangeResult>
  notify?: TxnNotifier
}

interface Props {
  project: string
  // Object the lookup is being opened from — carried into the picker request
  // so the server resolves and logs it against the right object. Visuals and
  // dashboards have none; a transaction's filter bar passes its own.
  objName?: string
  promptVars: VisualCriteriaPromptVar[]
  // Current values keyed by prompt-var name (canonical, e.g. "FROM_DATE").
  values: Record<string, string>
  // Single-field edit (use a functional setState in the host so the bulk
  // item_change updates below compose correctly).
  onChange: (name: string, value: string) => void
  // Enter pressed in a field → host runs the visual.
  onSubmit?: () => void
  runtime: VisualCriteriaRuntime
  // Findings from the last run's checks, shown under the criterion they name.
  issues?: CriteriaIssue[]
}

// Which HTML input a criterion's value is typed into. Driven by the data type
// the SERVER reported for the bound column, so the control can only ever be
// one the comparison accepts.
// A criterion's input box — the transaction form's own field look
// (FormRenderer `fieldStyle`): the input background (white in the light
// theme), a 1px border, a shaded read-only state. backgroundColor, never the
// `background` shorthand, which would wipe a <select>'s chevron (appTheme.ts).
function criteriaFieldStyle(locked: boolean): React.CSSProperties {
  return {
    width: '100%', boxSizing: 'border-box',
    padding: '7px 10px', borderRadius: 4,
    fontSize: '0.9625rem',
    border: '1px solid var(--color-border)',
    backgroundColor: locked
      ? 'var(--color-surface-2, var(--color-bg-secondary))'
      : 'var(--lp-input-bg, var(--color-input-bg, var(--color-bg)))',
    color: 'var(--color-text)',
  }
}

function inputType(dt?: string): string {
  switch (dt) {
    case 'date': return 'date'
    case 'datetime': return 'datetime-local'
    case 'time': return 'time'
    case 'number': return 'number'
    default: return 'text'
  }
}

function operatorOf(pv: { operator?: string }): string {
  return (pv.operator || '').trim().toLowerCase()
}

/** IN-list prompts carry their values as ONE comma-joined string (the format
 *  the BI engine splits server-side). */
function splitList(v: string | undefined): string[] {
  return (v ?? '').split(',').map((s) => s.trim()).filter(Boolean)
}

function toInputStr(v: unknown): string {
  if (v == null) return ''
  if (v instanceof Date) return v.toISOString().slice(0, 10)
  return String(v)
}

export default function VisualCriteriaForm({
  project, objName = '', promptVars, values, onChange, onSubmit, runtime, issues,
}: Props) {
  // Which criterion's lookup picker is open (by name), or null.
  const [lookupFor, setLookupFor] = useState<string | null>(null)
  // Dynamic protect overrides returned by item_change (1 = lock the input).
  const [protect, setProtect] = useState<Record<string, 0 | 1>>({})
  const notify = runtime.notify

  // LookupModal reads its transport from TxnRuntime context. A visual criteria
  // host only provides `lookup` (+ notify) — the master-record "add" flow is
  // disabled (allowAdd={false}) so the other members are never reached. Stub
  // them so the type is satisfied without dragging in save/deploy transport.
  const txnRt = useMemo<TxnRuntime>(() => {
    const unavailable = (name: string) => () => {
      throw new Error(`${name} is not available for visual criteria lookups`)
    }
    return {
      lookup: runtime.lookup,
      notify: notify ?? { success: () => {}, error: () => {} },
      lookupSource: unavailable('lookupSource') as TxnRuntime['lookupSource'],
      resolveDefaults: unavailable('resolveDefaults') as TxnRuntime['resolveDefaults'],
      save: unavailable('save') as TxnRuntime['save'],
      deploy: unavailable('deploy') as TxnRuntime['deploy'],
      uploadDocument: unavailable('uploadDocument') as TxnRuntime['uploadDocument'],
      runAction: unavailable('runAction') as TxnRuntime['runAction'],
    }
  }, [runtime.lookup, notify])

  // Fire the on-change rule attached to `name` (if any) and apply its result.
  const fireItemChange = useCallback(
    async (pv: VisualCriteriaPromptVar, latest: Record<string, string>) => {
      if (!pv.has_item_change || !runtime.criteriaItemChange) return
      try {
        const res = await runtime.criteriaItemChange(pv.name, { ...latest })
        if (res?.error) { notify?.error(res.error); return }
        for (const [k, v] of Object.entries(res?.updates || {})) {
          if (k === pv.name) continue // never echo the trigger back
          onChange(k, toInputStr(v))
        }
        if (res?.protect && Object.keys(res.protect).length) {
          setProtect((p) => ({ ...p, ...res.protect }))
        }
        if (res?.warning) (notify?.info ?? notify?.success)?.(res.warning)
      } catch (e) {
        notify?.error(`Could not apply ${pv.label}: ${(e as Error).message}`)
      }
    },
    [runtime, onChange, notify],
  )

  const openPv = lookupFor ? promptVars.find((p) => p.name === lookupFor) : null

  // The caption, with a * when the criterion must have a value.
  const caption = (pv: VisualCriteriaPromptVar) => (
    <>
      {pv.label}
      {pv.required && (
        <span aria-label="required" title="Required" style={{ color: 'var(--color-error)', marginLeft: 3 }}>*</span>
      )}
    </>
  )
  // The last run's findings on this criterion: an error in the error colour,
  // a warning / prompt in the secondary text colour (they did not stop it).
  const issueLine = (pv: VisualCriteriaPromptVar) => {
    const found = issuesFor(issues, pv.name)
    if (!found.length) return null
    return (
      <span {...tw('msg', 'criteria', { field: pv.name })} role="alert" style={{ display: 'grid', gap: 2 }}>
        {found.map((i, n) => (
          <span key={n} style={{
            fontSize: 13,
            color: (i.severity || 'E') === 'E' ? 'var(--color-error)' : 'var(--color-text-secondary)',
          }}>
            {i.message}
          </span>
        ))}
      </span>
    )
  }
  const hasLookup = (pv: VisualCriteriaPromptVar) =>
    !!((pv.lookup || '').trim() || (pv.lookup_sql || '').trim())

  return (
    <>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '12px 18px',
      }}>
        {promptVars.filter((pv) => !pv.hidden).map((pv) => {
          const locked = protect[pv.name] === 1 || !!pv.locked || !!pv.protect
          const lookupable = hasLookup(pv)
          const options = pv.options || []
          const operator = operatorOf(pv)
          // A fixed dropdown wins over a free-text input / lookup picker —
          // except for a RANGE, which needs two of them (handled below).
          if (options.length && operator !== 'between') {
            return (
              <label key={pv.name} {...tw('field', pv.name, { criteria: true, locked })} style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  {caption(pv)}
                </span>
                <select
                  value={values[pv.name] ?? ''}
                  disabled={locked}
                  onChange={(e) => {
                    onChange(pv.name, e.target.value)
                    void fireItemChange(pv, { ...values, [pv.name]: e.target.value })
                  }}
                  style={{ ...criteriaFieldStyle(locked), cursor: locked ? 'default' : 'pointer' }}
                >
                  <option value="">{pv.placeholder || '(any)'}</option>
                  {options.map((o, i) => (
                    <option key={`${o.data}-${i}`} value={o.data}>
                      {o.display || o.data}
                    </option>
                  ))}
                </select>
              {issueLine(pv)}
              </label>
            )
          }
          const op = operator
          const fieldStyle = criteriaFieldStyle(locked)
          // ── BETWEEN — two inputs (From / To), stored "lo,hi" (the
          // comma-joined format the engine splits into the two binds). ──
          if (op === 'between') {
            const parts = (values[pv.name] ?? '').split(',')
            const lo = (parts[0] ?? '').trim()
            const hi = (parts[1] ?? '').trim()
            const setPart = (pos: 0 | 1, v: string) => {
              const nl = pos === 0 ? v.trim() : lo
              const nh = pos === 1 ? v.trim() : hi
              onChange(pv.name, nl || nh ? `${nl},${nh}` : '')
            }
            // What each end of the range calls itself. A range is prompted as
            // two questions, so each may be captioned in its own right; the
            // bound that says nothing keeps the plain "From" / "To".
            const boundLabel = (pos: 0 | 1) =>
              (pos === 0 ? pv.label_from : pv.label_to)?.trim()
              || (pos === 0 ? 'From' : 'To')
            // A captioned bound says so ABOVE its input. It cannot say it in a
            // placeholder: a range over a date renders `type="date"`, and a
            // date input shows the browser's own dd/mm/yyyy widget and never a
            // placeholder — so on exactly the ranges that carry captions, the
            // caption would be invisible. Uncaptioned ranges keep the single
            // row of From/To placeholders they have always had.
            const showBoundLabels = !!(
              pv.label_from?.trim() || pv.label_to?.trim()
            )
            const withBoundLabel = (pos: 0 | 1, field: React.ReactNode) => (
              showBoundLabels ? (
                <div key={pos} style={{
                  flex: 1, minWidth: 0, display: 'grid', gap: 2,
                }}>
                  <span style={{
                    fontSize: 13, color: 'var(--color-text-secondary)',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {boundLabel(pos)}
                  </span>
                  {field}
                </div>
              ) : field
            )
            return (
              <label key={pv.name} {...tw('field', pv.name, { criteria: true, locked })} style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  {caption(pv)}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {([0, 1] as const).map((pos) => withBoundLabel(pos,
                    // A bound is one of whatever the column's single input is:
                    // a range over a picklist picks its two ends from the SAME
                    // list, rather than asking the user to spell a code.
                    options.length ? (
                      <select
                        key={pos}
                        value={pos === 0 ? lo : hi}
                        disabled={locked}
                        title={`${pv.label} — ${boundLabel(pos)}`}
                        onChange={(e) => {
                          setPart(pos, e.target.value)
                          void fireItemChange(pv, values)
                        }}
                        style={{ ...fieldStyle, flex: 1, minWidth: 0 }}
                      >
                        <option value="">{boundLabel(pos)}</option>
                        {options.map((o, i) => (
                          <option key={`${o.data}-${i}`} value={o.data}>
                            {o.display || o.data}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        key={pos}
                        type={inputType(pv.data_type)}
                        value={pos === 0 ? lo : hi}
                        disabled={locked}
                        placeholder={boundLabel(pos)}
                        title={`${pv.label} — ${boundLabel(pos)}`}
                        onChange={(e) => setPart(pos, e.target.value)}
                        onBlur={() => void fireItemChange(pv, values)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            void fireItemChange(pv, values)
                            onSubmit?.()
                          }
                        }}
                        style={{ ...fieldStyle, flex: 1, minWidth: 0 }}
                      />
                    )
                  ))}
                </div>
              {issueLine(pv)}
              </label>
            )
          }
          // ── IN — chip input (each chip one list value), stored comma-
          // joined; the lookup (when configured) multi-selects. ──
          if (op === 'in' || op === 'not in') {
            const list = splitList(values[pv.name])
            const setList = (next: string[]) => {
              const joined = next.join(',')
              onChange(pv.name, joined)
              void fireItemChange(pv, { ...values, [pv.name]: joined })
            }
            return (
              <label key={pv.name} {...tw('field', pv.name, { criteria: true, locked })} style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  {caption(pv)}
                </span>
                <div style={{ position: 'relative', display: 'flex', minWidth: 0 }}>
                  <ChipInput
                    values={list}
                    onChange={setList}
                    disabled={locked}
                    inputType={inputType(pv.data_type)}
                    placeholder="Add value…"
                    onEntryKeyDown={(e) => {
                      if (e.key === 'F2' && lookupable) {
                        e.preventDefault()
                        setLookupFor(pv.name)
                      }
                    }}
                    style={{ flex: 1, paddingRight: lookupable ? 30 : 6 }}
                  />
                  {lookupable && (
                    <button
                      type="button"
                      onClick={() => setLookupFor(pv.name)}
                      disabled={locked}
                      title={`Look up ${pv.label} (F2) — pick multiple`}
                      aria-label={`Look up ${pv.label}`}
                      style={{
                        position: 'absolute', right: 2, top: 2, bottom: 2,
                        width: 26, display: 'inline-flex', alignItems: 'center',
                        justifyContent: 'center', border: 'none', borderRadius: 4,
                        background: 'transparent', cursor: locked ? 'default' : 'pointer',
                        color: 'var(--color-text-secondary)',
                      }}
                    >
                      <Search size={14} />
                    </button>
                  )}
                </div>
              {issueLine(pv)}
              </label>
            )
          }
          return (
            <label key={pv.name} {...tw('field', pv.name, { criteria: true, locked })} style={{ display: 'grid', gap: 4, minWidth: 0 }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {caption(pv)}
              </span>
              <div style={{ position: 'relative', display: 'flex' }}>
                <input
                  type={inputType(pv.data_type)}
                  value={values[pv.name] ?? ''}
                  disabled={locked}
                  placeholder={pv.placeholder || ''}
                  onChange={(e) => onChange(pv.name, e.target.value)}
                  onBlur={() => void fireItemChange(pv, values)}
                  onKeyDown={(e) => {
                    if (e.key === 'F2' && lookupable) {
                      e.preventDefault()
                      setLookupFor(pv.name)
                    } else if (e.key === 'Enter') {
                      void fireItemChange(pv, values)
                      onSubmit?.()
                    }
                  }}
                  style={{ ...fieldStyle, paddingRight: lookupable ? 30 : 10 }}
                />
                {lookupable && (
                  <button
                    type="button"
                    onClick={() => setLookupFor(pv.name)}
                    disabled={locked}
                    title={`Look up ${pv.label} (F2)`}
                    aria-label={`Look up ${pv.label}`}
                    style={{
                      position: 'absolute', right: 2, top: 2, bottom: 2,
                      width: 26, display: 'inline-flex', alignItems: 'center',
                      justifyContent: 'center', border: 'none', borderRadius: 4,
                      background: 'transparent', cursor: locked ? 'default' : 'pointer',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    <Search size={14} />
                  </button>
                )}
              </div>
            {issueLine(pv)}
            </label>
          )
        })}
      </div>

      {openPv && (
        <TxnRuntimeProvider value={txnRt}>
          <LookupModal
            open
            project={project}
            objName={objName}
            lookup={openPv.lookup || ''}
            fieldLabel={openPv.label}
            title={openPv.lookup_title}
            allowAdd={false}
            filter={openPv.lookup_filter}
            filterRules={openPv.lookup_filter_rules}
            lookupSql={openPv.lookup_sql}
            dataCol={openPv.lookup_sql_data_col}
            formValues={values}
            // IN criteria multi-select in the picker — Apply hands back the
            // whole set, stored comma-joined like manual chip entry.
            multi={['in', 'not in'].includes(operatorOf(openPv))}
            selectedValues={splitList(values[openPv.name])}
            onSelectMany={(rows: LookupRow[]) => {
              const joined = rows
                .map((r) => toInputStr(r.data))
                .filter(Boolean)
                .join(',')
              onChange(openPv.name, joined)
              setLookupFor(null)
              void fireItemChange(openPv, { ...values, [openPv.name]: joined })
            }}
            onClose={() => setLookupFor(null)}
            onSelect={(row: LookupRow) => {
              onChange(openPv.name, toInputStr(row.data))
              setLookupFor(null)
              void fireItemChange(openPv, { ...values, [openPv.name]: toInputStr(row.data) })
            }}
          />
        </TxnRuntimeProvider>
      )}
    </>
  )
}
